import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Radio, Users, Eye, Heart, Gift, Crown, Star, Flame,
  Search, Filter, MapPin, Sparkles, TrendingUp, Play,
  ChevronRight, X, Volume2, Globe, Zap
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stream Categories
const CATEGORIES = [
  { id: 'all', name: 'All', icon: '🔥', color: '#FF2A2A' },
  { id: 'popular', name: 'Popular', icon: '⭐', color: '#FFE800' },
  { id: 'nearby', name: 'Nearby', icon: '📍', color: '#00F0FF' },
  { id: 'gaming', name: 'Gaming', icon: '🎮', color: '#8B5CF6' },
  { id: 'music', name: 'Music', icon: '🎵', color: '#EC4899' },
  { id: 'chat', name: 'Chat', icon: '💬', color: '#3B82F6' },
  { id: 'talent', name: 'Talent', icon: '🌟', color: '#F59E0B' },
  { id: 'dance', name: 'Dance', icon: '💃', color: '#10B981' },
];

// Live Stream Card Component
const LiveStreamCard = ({ stream, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  const viewerCount = stream.viewer_count || Math.floor(Math.random() * 5000) + 100;
  const formattedViewers = viewerCount >= 1000 
    ? `${(viewerCount / 1000).toFixed(1)}K` 
    : viewerCount;

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={() => onClick?.(stream)}
      className="relative rounded-2xl overflow-hidden cursor-pointer group"
      data-testid={`live-stream-${stream.id}`}
    >
      {/* Thumbnail */}
      <div className="aspect-[3/4] bg-gradient-to-br from-[#1a1a2e] to-[#0F0F16] relative">
        {stream.thumbnail ? (
          <img
            src={stream.thumbnail}
            alt={stream.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#00F0FF]/30 to-[#FF2A2A]/30 flex items-center justify-center">
              <span className="text-4xl">
                {stream.streamer_avatar || stream.streamer_name?.[0]?.toUpperCase() || '?'}
              </span>
            </div>
          </div>
        )}

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

        {/* Live Badge */}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <div className="flex items-center gap-1 px-2 py-1 bg-[#FF2A2A] rounded-md">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            <span className="text-xs font-bold text-white">LIVE</span>
          </div>
        </div>

        {/* Viewer Count */}
        <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-black/60 backdrop-blur-sm rounded-md">
          <Eye className="w-3 h-3 text-white" />
          <span className="text-xs font-medium text-white">{formattedViewers}</span>
        </div>

        {/* Gift indicator */}
        {stream.has_gifts && (
          <div className="absolute top-12 right-3 flex items-center gap-1 px-2 py-1 bg-[#FFE800]/20 backdrop-blur-sm rounded-md">
            <Gift className="w-3 h-3 text-[#FFE800]" />
          </div>
        )}

        {/* Hover play button */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="absolute inset-0 flex items-center justify-center bg-black/30"
            >
              <div className="w-16 h-16 rounded-full bg-[#00F0FF]/90 flex items-center justify-center">
                <Play className="w-8 h-8 text-black ml-1" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bottom info */}
        <div className="absolute bottom-0 left-0 right-0 p-3">
          {/* Streamer info */}
          <div className="flex items-center gap-2 mb-2">
            <img
              src={stream.streamer_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(stream.streamer_name || 'Live')}&background=0F0F16&color=00F0FF`}
              alt={stream.streamer_name}
              className="w-8 h-8 rounded-full border-2 border-[#00F0FF]"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate flex items-center gap-1">
                {stream.streamer_name || 'Streamer'}
                {stream.is_verified && (
                  <Crown className="w-3 h-3 text-[#FFE800]" />
                )}
              </p>
            </div>
          </div>

          {/* Stream title */}
          <p className="text-xs text-gray-300 truncate">{stream.title || 'Live Stream'}</p>

          {/* Category tag */}
          {stream.category && (
            <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 bg-white/10 rounded-full">
              <span className="text-xs">{CATEGORIES.find(c => c.id === stream.category)?.icon || '🎬'}</span>
              <span className="text-[10px] text-gray-300 capitalize">{stream.category}</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Featured Stream Banner
const FeaturedBanner = ({ stream, onClick }) => {
  if (!stream) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={() => onClick?.(stream)}
      className="relative rounded-3xl overflow-hidden cursor-pointer mb-6"
    >
      <div className="aspect-[21/9] bg-gradient-to-br from-[#1a1a2e] to-[#0F0F16] relative">
        {stream.thumbnail ? (
          <img
            src={stream.thumbnail}
            alt={stream.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-r from-[#FF2A2A]/20 via-[#FFE800]/20 to-[#00F0FF]/20" />
        )}

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />

        {/* Content */}
        <div className="absolute inset-0 p-6 flex flex-col justify-end">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex items-center gap-1 px-3 py-1 bg-[#FF2A2A] rounded-full">
              <Flame className="w-4 h-4 text-white" />
              <span className="text-sm font-bold text-white">HOT</span>
            </div>
            <div className="flex items-center gap-1 px-3 py-1 bg-black/40 rounded-full">
              <Eye className="w-4 h-4 text-white" />
              <span className="text-sm text-white">{stream.viewer_count || '2.5K'}</span>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-white mb-2">{stream.title || 'Featured Live Stream'}</h2>

          <div className="flex items-center gap-3">
            <img
              src={stream.streamer_avatar || `https://ui-avatars.com/api/?name=Featured&background=FF2A2A&color=fff`}
              alt={stream.streamer_name}
              className="w-10 h-10 rounded-full border-2 border-white"
            />
            <div>
              <p className="text-white font-semibold flex items-center gap-1">
                {stream.streamer_name || 'Featured Streamer'}
                <Crown className="w-4 h-4 text-[#FFE800]" />
              </p>
              <p className="text-gray-400 text-sm">{stream.category || 'Entertainment'}</p>
            </div>
          </div>
        </div>

        {/* Watch button */}
        <div className="absolute bottom-6 right-6">
          <button className="px-6 py-3 bg-[#00F0FF] text-black font-bold rounded-full flex items-center gap-2 hover:shadow-[0_0_30px_rgba(0,240,255,0.5)] transition-all">
            <Play className="w-5 h-5" />
            Watch Now
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Main Live Home Component
const LiveHome = ({ onWatchStream, onGoLive }) => {
  const { user } = useAuth();
  const [activeCategory, setActiveCategory] = useState('all');
  const [streams, setStreams] = useState([]);
  const [featuredStream, setFeaturedStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch live streams
  const fetchStreams = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/streams/live`, {
        params: {
          category: activeCategory !== 'all' ? activeCategory : undefined,
          limit: 20
        }
      });
      
      const liveStreams = response.data || [];
      
      // If no live streams, show mock demo data
      if (liveStreams.length === 0) {
        generateMockStreams();
      } else {
        setStreams(liveStreams);
        // Set featured stream (first one with most viewers)
        const sorted = [...liveStreams].sort((a, b) => (b.viewer_count || 0) - (a.viewer_count || 0));
        setFeaturedStream(sorted[0]);
      }
    } catch (error) {
      console.error('Error fetching streams:', error);
      // Generate mock streams for demo
      generateMockStreams();
    } finally {
      setLoading(false);
    }
  }, [activeCategory]);

  // Generate mock streams for demo
  const generateMockStreams = () => {
    const mockNames = [
      'GamerPro', 'MusicVibes', 'TalkShow', 'DanceQueen', 'ArtistLife',
      'FoodLover', 'TechGeek', 'FitnessFan', 'TravelBug', 'ComedyKing',
      'SingerStar', 'DJMaster', 'YogaGuru', 'ChefMike', 'StreamerX'
    ];
    
    const categories = ['gaming', 'music', 'chat', 'talent', 'dance'];
    const titles = [
      'Playing ranked matches! Come hang out 🎮',
      'Live music session - requests welcome! 🎵',
      'Late night chat - Ask me anything 💬',
      'Dance practice stream 💃',
      'Art creation live! 🎨',
      'Cooking dinner together 🍳',
      'Tech talk and Q&A 💻',
      'Morning workout routine 💪',
      'Travel stories from Japan ✈️',
      'Stand-up comedy night 😂'
    ];

    const mockStreams = mockNames.map((name, index) => ({
      id: `mock-${index}`,
      streamer_name: name,
      streamer_avatar: null,
      title: titles[index % titles.length],
      category: categories[index % categories.length],
      viewer_count: Math.floor(Math.random() * 10000) + 50,
      is_verified: index < 5,
      has_gifts: Math.random() > 0.5,
      thumbnail: null
    }));

    setStreams(mockStreams);
    setFeaturedStream(mockStreams[0]);
  };

  useEffect(() => {
    fetchStreams();
    
    // Refresh streams every 30 seconds
    const interval = setInterval(fetchStreams, 30000);
    return () => clearInterval(interval);
  }, [fetchStreams]);

  // Filter streams by search
  const filteredStreams = streams.filter(stream => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      stream.streamer_name?.toLowerCase().includes(query) ||
      stream.title?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="min-h-screen bg-[#030305] pb-24 overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#030305]/90 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="px-4 py-3 max-w-full">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Radio className="w-6 h-6 text-[#FF2A2A]" />
                <h1 className="text-xl font-bold text-white">Live</h1>
              </div>
              <div className="flex items-center gap-1 px-2 py-1 bg-[#FF2A2A]/20 rounded-full">
                <span className="w-2 h-2 bg-[#FF2A2A] rounded-full animate-pulse" />
                <span className="text-xs text-[#FF2A2A] font-medium">{streams.length} Live</span>
              </div>
            </div>

            {/* Go Live Button */}
            <button
              onClick={onGoLive}
              className="px-4 py-2 bg-gradient-to-r from-[#FF2A2A] to-[#FF6B6B] text-white font-semibold rounded-full flex items-center gap-2 hover:shadow-[0_0_20px_rgba(255,42,42,0.4)] transition-all"
              data-testid="go-live-btn"
            >
              <Radio className="w-4 h-4" />
              Go Live
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative mb-4">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search streamers..."
              className="w-full pl-10 pr-4 py-2.5 bg-[#0F0F16] border border-white/10 rounded-full text-sm text-white placeholder-gray-500 outline-none focus:border-[#00F0FF] transition-colors"
              data-testid="search-streams"
            />
          </div>

          {/* Categories */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                  activeCategory === cat.id
                    ? 'bg-[#00F0FF] text-black font-semibold'
                    : 'bg-[#0F0F16] text-gray-400 hover:text-white'
                }`}
                data-testid={`category-${cat.id}`}
              >
                <span>{cat.icon}</span>
                <span className="text-sm">{cat.name}</span>
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="px-4 py-4">
        {/* Featured Stream */}
        {featuredStream && !searchQuery && (
          <FeaturedBanner stream={featuredStream} onClick={onWatchStream} />
        )}

        {/* Loading State */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="aspect-[3/4] rounded-2xl bg-[#0F0F16] animate-pulse" />
            ))}
          </div>
        ) : filteredStreams.length === 0 ? (
          /* Empty State */
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-24 h-24 bg-[#0F0F16] rounded-full flex items-center justify-center mb-4">
              <Radio className="w-12 h-12 text-gray-600" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">No Live Streams</h3>
            <p className="text-gray-500 text-center max-w-md mb-6">
              {searchQuery 
                ? `No streams found for "${searchQuery}"`
                : 'No one is live right now. Be the first to go live!'}
            </p>
            <button
              onClick={onGoLive}
              className="px-6 py-3 bg-[#FF2A2A] text-white font-semibold rounded-full flex items-center gap-2"
            >
              <Radio className="w-5 h-5" />
              Start Streaming
            </button>
          </div>
        ) : (
          /* Stream Grid */
          <>
            {/* Section Header */}
            {!searchQuery && (
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#FFE800]" />
                  {activeCategory === 'all' ? 'Live Now' : CATEGORIES.find(c => c.id === activeCategory)?.name}
                </h2>
                <span className="text-sm text-gray-500">{filteredStreams.length} streaming</span>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {filteredStreams.map((stream, index) => (
                <LiveStreamCard
                  key={stream.id || index}
                  stream={stream}
                  onClick={onWatchStream}
                />
              ))}
            </div>
          </>
        )}

        {/* Quick Stats */}
        {!loading && streams.length > 0 && !searchQuery && (
          <div className="mt-8 grid grid-cols-3 gap-3">
            <div className="bg-[#0F0F16] rounded-2xl p-4 text-center">
              <div className="text-2xl font-bold text-[#FF2A2A]">{streams.length}</div>
              <div className="text-xs text-gray-500">Live Now</div>
            </div>
            <div className="bg-[#0F0F16] rounded-2xl p-4 text-center">
              <div className="text-2xl font-bold text-[#00F0FF]">
                {streams.reduce((acc, s) => acc + (s.viewer_count || 0), 0).toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">Watching</div>
            </div>
            <div className="bg-[#0F0F16] rounded-2xl p-4 text-center">
              <div className="text-2xl font-bold text-[#FFE800]">
                {streams.filter(s => s.is_verified).length}
              </div>
              <div className="text-xs text-gray-500">Verified</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveHome;
